import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Download,
  FileSignature,
  Info,
  Loader2,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import ContractSignaturePanel from "@/components/contracts/ContractSignaturePanel";
import { CONTRACT_STATE_LABELS } from "@/pages/admin/AdminContracts";
import { api, formatApiError } from "@/lib/api";
import { SignedLink } from "@/lib/signedFile";
import { useAuth } from "@/context/AuthContext";

const STATE_TONES = {
  SIGNED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  AWAITING_STUDENT_SIGNATURE: "bg-amber-50 text-amber-800 border-amber-200",
  FINALIZING: "bg-sky-50 text-sky-700 border-sky-200",
  FINALIZATION_FAILED: "bg-rose-50 text-rose-700 border-rose-200",
  EXPIRED: "bg-slate-100 text-slate-600 border-slate-200",
  TERMINATED: "bg-slate-100 text-slate-600 border-slate-200",
  SUPERSEDED: "bg-slate-100 text-slate-600 border-slate-200",
  VOID: "bg-slate-100 text-slate-500 border-slate-200",
};

const CURRENT_STATES = new Set([
  "AWAITING_STUDENT_SIGNATURE",
  "FINALIZING",
  "FINALIZATION_FAILED",
  "SIGNED",
]);

function StateBadge({ state }) {
  const tone = STATE_TONES[state] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {CONTRACT_STATE_LABELS[state] || state || "—"}
    </span>
  );
}

function formatShortDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium" }).format(new Date(value));
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function projectNameOf(doc) {
  return (
    doc?.values?.project_name
    || doc?.title
    || "وثيقة التزام التدريب التعاوني"
  );
}

export default function StudentContracts() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/documents");
      // Hard-filter to STUDENT_COMMITMENT even if backend returns extras.
      const scDocs = (data.documents || []).filter((d) => d.kind === "STUDENT_COMMITMENT");
      setDocuments(scDocs);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const doc = params.get("document");
    if (doc) setOpenId(doc);
  }, [location.search]);

  const closeDrawer = useCallback(() => {
    setOpenId(null);
    const params = new URLSearchParams(location.search);
    if (params.get("document")) {
      params.delete("document");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const { current, history } = useMemo(() => {
    const cur = documents.filter((d) => CURRENT_STATES.has(d.state));
    const hist = documents.filter((d) => !CURRENT_STATES.has(d.state));
    return { current: cur, history: hist };
  }, [documents]);

  return (
    <DashboardLayout>
      <div data-testid="student-contracts" dir="rtl">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
              تعهداتي التدريبية
            </div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">
              العقود والتعهدات
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600">
              وثيقة التزام التدريب التعاوني يُنشأ تلقائيًا بعد قبول طلبك. راجع النص، وقّع إلكترونيًا،
              ونزّل النسخة النهائية عند اكتمال المعالجة.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="student-contracts-refresh"
          >
            <RefreshCw className="h-4 w-4" /> تحديث
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-24" data-testid="student-contracts-loading">
            <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
          </div>
        ) : documents.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {current.length > 0 && (
              <Section title="التعهد الحالي">
                <div className="grid gap-3 md:grid-cols-2">
                  {current.map((doc) => (
                    <ContractCard key={doc.id} doc={doc} onOpen={() => setOpenId(doc.id)} />
                  ))}
                </div>
              </Section>
            )}
            {history.length > 0 && (
              <Section title="السجل السابق">
                <div className="grid gap-3 md:grid-cols-2">
                  {history.map((doc) => (
                    <ContractCard key={doc.id} doc={doc} onOpen={() => setOpenId(doc.id)} />
                  ))}
                </div>
              </Section>
            )}
          </>
        )}

        {openId && (
          <ContractDetailDrawer
            documentId={openId}
            currentUser={user}
            onClose={closeDrawer}
            onChanged={load}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function ContractCard({ doc, onOpen }) {
  const dateLabel = doc.all_signatures_at || doc.finalized_at || doc.created_at;
  return (
    <article
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
      data-testid={`student-contract-card-${doc.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-500">وثيقة التزام التدريب التعاوني</div>
          <div className="mt-0.5 font-bold text-slate-900 line-clamp-1">
            {projectNameOf(doc)}
          </div>
          <div className="mt-1 font-mono text-xs text-slate-500" dir="ltr">
            {doc.number || doc.id.slice(-8)}
          </div>
        </div>
        <StateBadge state={doc.state} />
      </div>
      <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
        <div>الإصدار: <span className="font-semibold text-slate-800">{doc.version ?? 1}</span></div>
        <div>{doc.state === "SIGNED" ? "وُقِّع" : "أُنشئ"}: <span className="font-semibold text-slate-800">{formatShortDate(dateLabel)}</span></div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-slate-900 px-4 text-xs font-bold text-white hover:bg-slate-800"
          data-testid={`student-contract-open-${doc.id}`}
        >
          فتح التعهد
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
      <FileSignature className="h-10 w-10 text-slate-300" />
      <div className="mt-3 font-bold text-slate-800">لا توجد تعهدات حتى الآن</div>
      <div className="mt-1 max-w-md text-xs leading-6 text-slate-500">
        ستُنشأ وثيقة التزام التدريب التعاوني تلقائيًا بعد قبول أحد طلباتك أو تسكينك في فرصة تدريبية،
        وستصلك تنبيه لتوقيعه.
      </div>
    </div>
  );
}

function ContractDetailDrawer({ documentId, currentUser, onClose, onChanged }) {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/documents/${documentId}`);
      setDocument(data.document);
      setNotFound(false);
    } catch (error) {
      const code = error?.response?.status;
      if (code === 404 || code === 403) {
        setNotFound(true);
      } else {
        toast.error(formatApiError(error));
      }
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  const canSign =
    document?.my_signer_slot === "student"
      && document.state === "AWAITING_STUDENT_SIGNATURE";

  const isSigned = document?.state === "SIGNED";
  const isFinalizing = document?.state === "FINALIZING";
  const isFailed = document?.state === "FINALIZATION_FAILED";

  return (
    <div
      className="fixed inset-0 z-40 flex justify-start bg-slate-900/60 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-contract-dialog-title"
        tabIndex={-1}
        data-testid="student-contract-detail"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
              وثيقة التزام التدريب التعاوني
            </div>
            <h2 id="student-contract-dialog-title" className="mt-1 font-heading text-xl font-extrabold text-slate-900">
              {document ? projectNameOf(document) : "تفاصيل التعهد"}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span dir="ltr" className="font-mono">{document?.number || documentId}</span>
              {document?.state && <StateBadge state={document.state} />}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
            aria-label="إغلاق"
            data-testid="student-contract-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="grid place-items-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
            </div>
          ) : notFound ? (
            <div className="grid place-items-center py-20 text-center">
              <FileSignature className="h-10 w-10 text-slate-300" />
              <div className="mt-3 font-bold text-slate-800">تعذّر الوصول للتعهد</div>
              <div className="mt-1 max-w-sm text-xs leading-6 text-slate-500">
                إما أن الرابط غير صحيح أو أن هذا التعهد لا يخصك.
              </div>
            </div>
          ) : document ? (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <MetaCell label="أُنشئ في" value={formatDateTime(document.created_at)} />
                <MetaCell label="سريان من" value={formatShortDate(document.valid_from)} />
                <MetaCell label="سريان إلى" value={formatShortDate(document.valid_until)} />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-3 text-sm font-bold text-slate-800">
                  إثبات التوقيع
                </div>
                <div className="divide-y divide-slate-100">
                  {(document.signatures || []).length === 0 && (
                    <div className="p-4 text-sm text-slate-500">لم يوقّع أحد بعد.</div>
                  )}
                  {(document.signatures || []).map((sig) => (
                    <div key={sig.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <div className="text-sm font-bold text-slate-900">
                          {sig.slot === "student" ? "الطالب" : sig.slot === "siaq" ? "منصة سياق" : "الجمعية"}
                        </div>
                        <div className="text-xs text-slate-500">{sig.typed_name || "—"}</div>
                      </div>
                      <div className="text-xs text-slate-500">{formatDateTime(sig.signed_at)}</div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> موثّق
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-2 text-sm font-bold text-slate-800">نص التعهد الكامل</div>
                <div
                  className="max-h-[52vh] overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-8 text-slate-800"
                  dir="rtl"
                  data-testid="student-contract-text"
                >
                  {document.rendered_text || document.legal_text || "—"}
                </div>
              </div>

              {canSign && (
                <section data-testid="student-contract-signature-section" className="space-y-3">
                  <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-7 text-amber-900">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                    <div>
                      <div className="font-bold">قبل التوقيع</div>
                      <div className="mt-1">
                        اقرأ التعهد كاملًا قبل التوقيع. التوقيع يتطلب موافقتك الصريحة
                        ورمز تحقق يُرسل إلى بريد حسابك.
                      </div>
                    </div>
                  </div>
                  <ContractSignaturePanel
                    documentId={documentId}
                    expectedName={currentUser?.name}
                    onSigned={async () => {
                      await load();
                      onChanged?.();
                    }}
                  />
                </section>
              )}

              {isSigned && (
                <div
                  className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4"
                  data-testid="student-contract-signed"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                  <div className="text-sm leading-7 text-emerald-900">
                    <div className="font-bold">اكتمل توقيع وثيقة التزام التدريب التعاوني</div>
                    <div className="mt-1">
                      حُفظ إثبات توقيعك بشكل آمن، والوثيقة أصبحت جاهزة للتنزيل بصيغة PDF.
                    </div>
                  </div>
                </div>
              )}

              {isFinalizing && (
                <div className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm leading-7 text-sky-900">
                  <Info className="mt-0.5 h-5 w-5 shrink-0" />
                  جارٍ تجهيز النسخة النهائية.
                </div>
              )}

              {isFailed && (
                <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm leading-7 text-rose-900">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  تعذّر تجهيز النسخة النهائية، وتم إشعار إدارة المنصة. سنُنبّهك فور اكتمال المعالجة.
                </div>
              )}

              {document.artifact_file_id && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-emerald-800">تنزيل النسخة النهائية</div>
                      <div className="mt-1 text-xs text-emerald-700">
                        الملف مُخزّن في المخزن الآمن ويُخدَّم عبر رابط موقّع قصير الصلاحية.
                      </div>
                    </div>
                    <SignedLink
                      fileId={document.artifact_file_id}
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-bold text-white hover:bg-emerald-600"
                      testid="student-contract-download"
                    >
                      <Download className="h-4 w-4" />
                      تنزيل PDF
                    </SignedLink>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-800">{value}</div>
    </div>
  );
}
